import React, { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { useSelector } from "react-redux";
import _ from "lodash";

ChartJS.register(ArcElement, Tooltip, Legend);

/** When true (Dashboard row), chart + footer area keep fixed vertical space so loading/loaded don't jump. */
export function Chart({ metrics, dashboardEmbed = false }) {
  const theme = useSelector((state) => state.theme.mode);
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m === 0 ? `${h}H` : `${h}H ${m}M`;
  };

  useEffect(() => {
    if (!metrics) return;

    const data = {
      labels: ["Given Hours", "Leave Hours", "Outstanding Hours"],
      datasets: [
        {
          data: [metrics.GivenHours, metrics.LeaveHours, _.round(metrics.OutstandingHours * 60) / 60],
          backgroundColor: ["#059669", "#f43f5e", "#fbbf24"],
          hoverBackgroundColor: ["#059669", "#e11d48", "#f59e0b"],
          borderWidth: 0,
          hoverBorderWidth: 0,
          borderRadius: 10,
          spacing: 2,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: true,
      cutout: "82%",
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: theme === "dark" ? "#1e293b" : "#ffffff",
          titleColor: theme === "dark" ? "#f8fafc" : "#1e293b",
          bodyColor: theme === "dark" ? "#f8fafc" : "#1e293b",
          padding: 12,
          boxPadding: 6,
          usePointStyle: true,
          callbacks: {
            label: function (context) {
              const currentValue = context.raw;
              return ` ${context.label}: ${formatTime(currentValue)}`;
            },
          },
        },
      },
    };

    setChartData(data);
    setChartOptions(options);

    const centerTextPlugin = {
      id: "centerText",
      beforeDraw: function (chart) {
        const ctx = chart.ctx;
        const width = chart.width;
        const height = chart.height;
        ctx.restore();

        const text1 = formatTime(metrics.GivenHours);
        const text2 = formatTime(metrics.ExpectedHours);

        const fontSize1 = height / 7;
        const fontSize2 = height / 9;

        ctx.textBaseline = "middle";

        // Draw Text 1 (Given Hours)
        ctx.font = `bold ${fontSize1}px Inter, -apple-system, sans-serif`;
        ctx.fillStyle = "#10b981";
        const textX1 = (width - ctx.measureText(text1).width) / 2;
        const textY1 = height / 2 - fontSize1 / 1.8;
        ctx.fillText(text1, textX1, textY1);

        // Draw Text 2 (Expected Hours)
        ctx.font = `500 ${fontSize2}px Inter, -apple-system, sans-serif`;
        ctx.fillStyle = theme === "dark" ? "#60a5fa" : "#2563eb";
        const textX2 = (width - ctx.measureText(text2).width) / 2;
        const textY2 = height / 2 + fontSize2 / 0.8;
        ctx.fillText(text2, textX2, textY2);

        ctx.save();
      },
    };

    ChartJS.unregister(centerTextPlugin);
    ChartJS.register(centerTextPlugin);

  }, [metrics, theme]);

  const chartBoxClass =
    "w-[224px] h-[224px] m-1 shrink-0 flex items-center justify-center";
  const footerSlotClass =
    "mt-4 w-full min-h-[2.5rem] flex items-center justify-center text-sm text-center text-gray-700 dark:text-gray-400 px-1";

  if (_.isEmpty(metrics)) {
    return (
      <div
        className={`flex flex-col items-center w-full ${
          dashboardEmbed ? "min-h-[272px]" : "py-2"
        }`}
      >
        <div className={chartBoxClass}>
          <svg
            aria-hidden="true"
            className="w-12 h-12 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
        </div>
        {dashboardEmbed ? <div className={footerSlotClass} aria-hidden="true" /> : null}
      </div>
    );
  }

  const footer = (
    <p className="text-center">
      <svg
        className="w-6 h-6 text-green-300 dark:text-green-300 mr-2 inline-block -mt-1"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          fillRule="evenodd"
          d="M2 12a10 10 0 1 1 20 0 10 10 0 0 1-20 0Zm13.7-1.3a1 1 0 0 0-1.4-1.4L11 12.6l-1.8-1.8a1 1 0 0 0-1.4 1.4l2.5 2.5c.4.4 1 .4 1.4 0l4-4Z"
          clipRule="evenodd"
        />
      </svg>
      Better than
      <span className="font-bold text-black mr-1 dark:text-gray-300 text-base">
        {""}{" "}
        {metrics?.PerformanceRate && !isNaN(metrics.PerformanceRate)
          ? `${metrics.PerformanceRate}`
          : "0"}
      </span>
      employees!
    </p>
  );

  return (
    <div
      className={`flex flex-col items-center w-full ${
        dashboardEmbed ? "min-h-[272px]" : ""
      }`}
    >
      <div className={chartBoxClass}>
        {chartData.datasets && <Doughnut data={chartData} options={chartOptions} />}
      </div>
      <div
        className={
          dashboardEmbed ? footerSlotClass : "text-gray-700 text-sm text-center dark:text-gray-400 mt-4 shrink-0"
        }
      >
        {footer}
      </div>
    </div>
  );
}
